/**
 * DirectiveTrackerHUD — In-Match Micro-Tracker & Celebration Controller.
 * 
 * Displays:
 * - A compact, rolling in-match tracker chip in the top-left for active daily objectives.
 * - Cycles smoothly between all active uncompleted directives (e.g. 6.5s per objective).
 * - Celebratory completion state directly inside the chip when an objective is reached.
 */

import { directives } from '../engine/directives.js';
import { getIconSvg } from './icons.js';

export class DirectiveTrackerHUD {
  constructor(container = document.body, directivesModal = null) {
    this.container = container;
    this.directivesModal = directivesModal;
    this.chipEl = null;

    // Rolling cycle state
    this._activeIdx = 0;
    this._cycleTimer = 0;
    this._cycleInterval = 6.5; // seconds per directive before advancing

    // Celebration state (when a directive is completed in-match)
    this._celebrationTimer = 0;
    this._celebrationDirective = null;
    this._wasCollapsedBeforeCelebration = false;

    this._allDoneDismissed = false;

    // Mobile collapse & avatar docking state
    this._collapsed = true;
    this.playerTeam = 'western';
    this.disabled = false;

    this._initElements();
  }

  setDisabled(val) {
    this.disabled = !!val;
    if (this.disabled) {
      this.hideChip();
    }
  }

  _initElements() {
    let chip = document.getElementById('match-directive-chip');
    if (!chip) {
      chip = document.createElement('div');
      chip.id = 'match-directive-chip';
      chip.className = 'match-directive-chip hidden';
      this.container.appendChild(chip);
    }
    this.chipEl = chip;

    if (this.chipEl?.setAttribute) {
      this.chipEl.setAttribute('role', 'button');
      this.chipEl.setAttribute('aria-expanded', String(!this._collapsed));
      this.chipEl.setAttribute('aria-label', 'Daily Directives');
      this.chipEl.setAttribute('title', this._collapsed ? 'Expand Daily Directives' : 'Minimize Daily Directives');
    }

    if (this.chipEl?.addEventListener) {
      const handleTap = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target && e.target.closest && e.target.closest('.chip-eye-btn')) {
          this.openDirectivesModal();
        } else {
          this.toggleCollapse();
        }
      };
      this.chipEl.addEventListener('click', handleTap);
      this.chipEl.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      });
    }

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('resize', () => this.updatePosition(true));
      window.addEventListener('orientationchange', () => this.updatePosition(true));
    }

    this._applyStateClasses();
  }

  /**
   * Opens the full daily directives modal popup.
   */
  openDirectivesModal() {
    if (this.directivesModal && typeof this.directivesModal.open === 'function') {
      this.directivesModal.open();
    } else if (typeof window !== 'undefined' && window.tcgGame?.directivesModal?.open) {
      window.tcgGame.directivesModal.open();
    } else if (typeof document !== 'undefined') {
      const btn = document.getElementById('btn-open-directives');
      if (btn) btn.click();
    }
  }

  /**
   * Check if current viewport matches mobile layout.
   */
  isMobile() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 900px) and (orientation: portrait), (max-height: 560px) and (orientation: landscape)').matches;
  }

  /**
   * Toggle between expanded view and collapsed view docked under player avatar.
   * @param {boolean} [forceState]
   */
  toggleCollapse(forceState) {
    this._collapsed = forceState !== undefined ? forceState : !this._collapsed;
    this._applyStateClasses();
    if (this.chipEl?.setAttribute) {
      this.chipEl.setAttribute('aria-expanded', String(!this._collapsed));
      this.chipEl.setAttribute('title', this._collapsed ? 'Expand Daily Directives' : 'Minimize Daily Directives');
    }
    this.updatePosition(true);
  }

  /**
   * Updates player team to ensure avatar docking attaches to the correct player portrait.
   * @param {'western'|'eastern'} team
   */
  setPlayerTeam(team) {
    if (team) {
      this.playerTeam = team;
      this._applyStateClasses();
      this.updatePosition(true);
    }
  }

  _applyStateClasses() {
    if (!this.chipEl) return;
    this.chipEl.classList?.toggle('chip-collapsed', !!this._collapsed);
    const isEastern = this.playerTeam === 'eastern';
    this.chipEl.classList?.toggle('chip-player-western', !isEastern);
    this.chipEl.classList?.toggle('chip-player-eastern', isEastern);
  }

  /**
   * Dynamically aligns the chip with the player avatar (desktop & mobile).
   */
  updatePosition(force = false) {
    if (this.disabled) {
      this.hideChip();
      return;
    }
    if (!this.chipEl || typeof document === 'undefined' || !document.querySelector) return;

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (!force && this._lastPositionUpdate && (now - this._lastPositionUpdate) < 2000) {
      return;
    }
    this._lastPositionUpdate = now;

    this._applyStateClasses();

    const isEastern = this.playerTeam === 'eastern';
    const selector = isEastern ? '.boss-avatar.eastern' : '.boss-avatar.western';
    const avatarEl = document.querySelector(selector);
    const parentEl = this.chipEl.offsetParent || this.chipEl.parentElement;

    if (avatarEl && parentEl && avatarEl.getBoundingClientRect && parentEl.getBoundingClientRect) {
      const rect = avatarEl.getBoundingClientRect();
      const parentRect = parentEl.getBoundingClientRect();
      const topOffset = Math.round(rect.bottom - parentRect.top);
      const width = Math.round(rect.width);

      const setProp = (k, v) => {
        if (this.chipEl.style?.setProperty) this.chipEl.style.setProperty(k, v);
        else if (this.chipEl.style) this.chipEl.style[k] = v;
      };
      const remProp = (k) => {
        if (this.chipEl.style?.removeProperty) this.chipEl.style.removeProperty(k);
        else if (this.chipEl.style) delete this.chipEl.style[k];
      };

      setProp('--dock-top', `${topOffset}px`);
      setProp('--dock-width', `${width}px`);

      if (isEastern) {
        const rightOffset = Math.round(parentRect.right - rect.right);
        setProp('--dock-right', `${rightOffset}px`);
        remProp('--dock-left');
      } else {
        const leftOffset = Math.round(rect.left - parentRect.left);
        setProp('--dock-left', `${leftOffset}px`);
        remProp('--dock-right');
      }
    }
  }

  /**
   * Displays the celebration state directly inside the chip.
   * Pauses rolling cycle for 4.0s to showcase the reward and completion.
   */
  showCompletion(directive) {
    if (this.disabled || !directive) return;
    this._celebrationDirective = directive;
    this._celebrationTimer = 4.0;
    if (this._collapsed) {
      this._wasCollapsedBeforeCelebration = true;
      this.toggleCollapse(false);
    }
    this._renderCelebration(directive);
  }

  /**
   * Backward-compatible alias for showCompletion.
   */
  showToast(directive) {
    this.showCompletion(directive);
  }

  _renderCelebration(directive) {
    if (!this.chipEl || this.disabled) return;
    this.chipEl.classList.remove('hidden', 'chip-cycling');
    this.chipEl.classList.add('chip-completed');
    this._applyStateClasses();

    this.chipEl.innerHTML = `
      <div class="chip-body">
        <div class="chip-expanded-view">
          <div class="chip-header-row">
            <span class="chip-badge">✓ COMPLETE!</span>
            <div class="chip-actions">
              <button type="button" class="chip-eye-btn" title="Open Daily Quests details" aria-label="Open details">SHOW</button>
              <span class="chip-collapse-hint" aria-hidden="true" title="Collapse">▲</span>
            </div>
          </div>
          <div class="chip-content-row">
            <span class="chip-title" title="${directive.title}">${directive.title}</span>
          </div>
          <div class="chip-rewards">
            <span class="chip-reward-pill fugazi">+${directive.rewardFugazi} FUGAZI</span>
            ${directive.rewardRankBonus ? `<span class="chip-reward-pill rank">+${directive.rewardRankBonus} RANK</span>` : ''}
          </div>
        </div>
        <div class="chip-docked-view">
          <div class="chip-docked-meta">
            <span class="chip-docked-icon">✓</span>
            <span class="chip-docked-counter">+${directive.rewardFugazi}</span>
          </div>
          <div class="chip-docked-track">
            <div class="chip-docked-fill" style="width: 100%;"></div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Periodic update called every frame or evaluation tick.
   * @param {Object} tracker - MatchDirectiveTracker instance
   * @param {string} liveGrade - Current combat rank grade (e.g. 'A', 'S')
   * @param {number} dt - Elapsed seconds since last call (defaults to 1.0)
   * @param {'western'|'eastern'} [playerTeam] - Optional active player team
   */
  updateChip(tracker, liveGrade, dt = 1.0, playerTeam = null) {
    if (!this.chipEl || this.disabled) {
      if (this.disabled) this.hideChip();
      return;
    }

    if (playerTeam) {
      this.playerTeam = playerTeam;
    }
    this.updatePosition();

    // 1. If currently celebrating an achievement, decrement timer and hold celebration view
    if (this._celebrationTimer > 0) {
      this._celebrationTimer -= dt;
      if (this._celebrationTimer <= 0) {
        this._celebrationDirective = null;
        this.chipEl.classList.remove('chip-completed');
        this._cycleTimer = 0;
        if (this._wasCollapsedBeforeCelebration) {
          this._wasCollapsedBeforeCelebration = false;
          this.toggleCollapse(true);
        }
      } else {
        return; // Hold completion display
      }
    }

    // 2. Fetch uncompleted directives
    const uncompleted = directives.getDirectives().filter(d => !d.completed);

    if (uncompleted.length === 0) {
      if (!this._allDoneDismissed) {
        this._renderAllCompleted();
      } else {
        this.hideChip();
      }
      return;
    }

    this._allDoneDismissed = false;
    this.chipEl.classList.remove('hidden', 'chip-completed');

    // 3. Advance rolling cycle timer
    this._cycleTimer += dt;
    if (this._cycleTimer >= this._cycleInterval) {
      this._cycleTimer = 0;
      this._activeIdx = (this._activeIdx + 1) % uncompleted.length;

      // Quick CSS transition indicator
      this.chipEl.classList.add('chip-cycling');
      setTimeout(() => {
        if (this.chipEl) this.chipEl.classList.remove('chip-cycling');
      }, 220);
    }

    if (this._activeIdx >= uncompleted.length) {
      this._activeIdx = 0;
    }

    const activeDir = uncompleted[this._activeIdx];
    if (!activeDir) return;

    // 4. Compute progress string & bar percentage
    let progressStr = '';
    let pct = 0;

    if (activeDir.type === 'HOLD_TERRITORY') {
      const currentSec = Math.floor(tracker?.secAbove60Territory || 0);
      progressStr = `${currentSec}s / ${activeDir.target}s`;
      pct = Math.min(100, (currentSec / activeDir.target) * 100);
    } else if (activeDir.type === 'HIGH_RANK') {
      progressStr = `Rank: ${liveGrade || '--'}`;
      pct = (liveGrade === 'A' || liveGrade === 'S' || liveGrade === 'S+') ? 100 : (liveGrade === 'B' ? 60 : 30);
    } else if (activeDir.type === 'PLASMA_DISCIPLINE') {
      progressStr = tracker?.plasmaEverCapped ? 'Failed ✕' : 'Discipline Active ✓';
      pct = tracker?.plasmaEverCapped ? 0 : 100;
    } else if (activeDir.type === 'RAPID_PUSH') {
      if (tracker?.rapidPushCompleted) {
        progressStr = 'Achieved ✓';
        pct = 100;
      } else if ((tracker?.duration || 0) > 60) {
        progressStr = 'Time Expired ✕';
        pct = 0;
      } else {
        const peakPct = Math.round((tracker?.maxTerritoryFront || 0.5) * 100);
        progressStr = `${peakPct}% / 65%`;
        pct = Math.min(100, Math.round(((tracker?.maxTerritoryFront || 0.5) / 0.65) * 100));
      }
    } else if (activeDir.type === 'S_RANK_VICTORY') {
      progressStr = `Rank: ${liveGrade || '--'}`;
      pct = (liveGrade === 'S' || liveGrade === 'S+') ? 100 : 25;
    } else if (activeDir.type === 'BLITZKRIEG') {
      const dur = Math.floor(tracker?.duration || 0);
      if (dur > 270) {
        progressStr = 'Time Expired (>4:30) ✕';
        pct = 0;
      } else {
        const mins = Math.floor(dur / 60);
        const secs = dur % 60;
        progressStr = `${mins}:${String(secs).padStart(2, '0')} / 4:30`;
        pct = Math.min(100, Math.round((dur / 270) * 100));
      }
    } else if (activeDir.type === 'CONVENTIONAL_WAR') {
      const used = (tracker?.superweaponsUsed || 0) > 0;
      progressStr = used ? 'Failed ✕' : 'No Superweapon ✓';
      pct = used ? 0 : 100;
    } else if (activeDir.type === 'TITAN_WARFARE') {
      const heavy = tracker?.deckHeavyCount || 0;
      const recorded = !!(tracker?.deckProfileRecorded || tracker?.deckMaxCost > 0 || tracker?.deckHeavyCount > 0);
      if (!recorded || heavy >= 3) {
        progressStr = 'Deck Valid ✓';
        pct = 100;
      } else {
        progressStr = `Deck Invalid (${heavy}/3) ✕`;
        pct = 0;
      }
    } else if (activeDir.type === 'LOW_PROFILE') {
      const heavy = tracker?.deckHeavyCount || 0;
      const maxCost = tracker?.deckMaxCost || 0;
      const recorded = !!(tracker?.deckProfileRecorded || maxCost > 0 || heavy > 0);
      if (!recorded || (heavy === 0 && maxCost < 40)) {
        progressStr = 'Deck Valid ✓';
        pct = 100;
      } else {
        progressStr = 'Deck Invalid (40+ Card) ✕';
        pct = 0;
      }
    } else {
      progressStr = `${activeDir.progress} / ${activeDir.target}`;
      pct = Math.min(100, (activeDir.progress / activeDir.target) * 100);
    }

    const counterBadge = uncompleted.length > 1
      ? `QUEST ${this._activeIdx + 1}/${uncompleted.length}`
      : 'DAILY QUEST';
    const counterShort = uncompleted.length > 1
      ? `${this._activeIdx + 1}/${uncompleted.length}`
      : '1/1';

    this._applyStateClasses();

    this.chipEl.innerHTML = `
      <div class="chip-body">
        <div class="chip-expanded-view">
          <div class="chip-header-row">
            <span class="chip-badge">${counterBadge}</span>
            <div class="chip-actions">
              <button type="button" class="chip-eye-btn" title="Open Daily Quests details" aria-label="Open details">SHOW</button>
              <span class="chip-collapse-hint" aria-hidden="true" title="Collapse">▲</span>
            </div>
          </div>
          <div class="chip-content-row">
            <span class="chip-title" title="${activeDir.title}">${activeDir.title}</span>
            <span class="chip-val">${progressStr}</span>
          </div>
          <div class="chip-track">
            <div class="chip-fill" style="width: ${pct}%;"></div>
          </div>
        </div>
        <div class="chip-docked-view" title="${activeDir.title}: ${progressStr}">
          <div class="chip-docked-meta">
            <span class="chip-docked-icon">${getIconSvg('target', 12)}</span>
            <span class="chip-docked-counter">${counterShort}</span>
          </div>
          <div class="chip-docked-track">
            <div class="chip-docked-fill" style="width: ${pct}%;"></div>
          </div>
        </div>
      </div>
    `;
  }

  _renderAllCompleted() {
    if (!this.chipEl) return;
    this.chipEl.classList.remove('hidden', 'chip-cycling');
    this.chipEl.classList.add('chip-completed');
    this._applyStateClasses();

    this.chipEl.innerHTML = `
      <div class="chip-body">
        <div class="chip-expanded-view">
          <div class="chip-header-row">
            <span class="chip-badge">★ ALL COMPLETE!</span>
            <div class="chip-actions">
              <button type="button" class="chip-eye-btn" title="Open Daily Quests details" aria-label="Open details">SHOW</button>
              <span class="chip-collapse-hint" aria-hidden="true" title="Collapse">▲</span>
            </div>
          </div>
          <div class="chip-content-row">
            <span class="chip-title">ALL DAILY QUESTS COMPLETED</span>
          </div>
          <div class="chip-rewards">
            <span class="chip-reward-pill fugazi">+100 FUGAZI JACKPOT</span>
          </div>
        </div>
        <div class="chip-docked-view">
          <div class="chip-docked-meta">
            <span class="chip-docked-icon">★</span>
            <span class="chip-docked-counter">MAX</span>
          </div>
          <div class="chip-docked-track">
            <div class="chip-docked-fill" style="width: 100%;"></div>
          </div>
        </div>
      </div>
    `;

    // Automatically fade out after 6 seconds
    if (!this._allDoneTimeout) {
      this._allDoneTimeout = setTimeout(() => {
        this._allDoneDismissed = true;
        this.hideChip();
      }, 6000);
    }
  }

  hideChip() {
    if (this.chipEl && !this.chipEl.classList.contains('hidden')) {
      this.chipEl.classList.add('hidden');
    }
  }
}
